/**
 * mapPlotter.ts
 *
 * Office.js layer that inserts the map slide, plots marker shapes, and shades
 * regions on the active PowerPoint slide. Guards host access with `hasPpt()` /
 * `ppt()` and reports diagnostics through the shared `src/telemetry` pipeline
 * (`logEvent` / `logError`). Operates on the *selected* slide.
 *
 * Requires PowerPointApi 1.4 (addGeometricShape); region-group reads need 1.8.
 */

import { logEvent } from "../../../../../telemetry";
import { ProjectionEngine } from "./projectionEngine";
import type {
  Calibration,
  MapLocation,
  MapProfile,
  MarkerConfig,
  MarkerShape,
  PlotResult,
  ShapeBounds,
  SlideShapeInfo,
} from "../types";

/**
 * Map operations belong to the Deckorate connector; tag every telemetry event
 * with this `connectorSystemName` so they slice under the "integration" backend.
 */
const MAP_CONNECTOR = "deckorate";

const MARKER_BATCH_SIZE = 50;
const MARKER_PREFIX = "Marker: ";
const LABEL_PREFIX = "Label: ";

const engine = new ProjectionEngine();

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

function ensurePpt(): void {
  if (!hasPpt()) throw new Error("Map plotting requires running inside PowerPoint.");
}

/** PowerPointApi 1.4 check (addGeometricShape). */
export function isMapPlottingSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", "1.4");
  } catch {
    return false;
  }
}

function markerShapeType(shape: MarkerShape): PowerPoint.GeometricShapeType {
  switch (shape) {
    case "diamond":
      return PowerPoint.GeometricShapeType.diamond;
    case "triangle":
      return PowerPoint.GeometricShapeType.triangle;
    case "rectangle":
      return PowerPoint.GeometricShapeType.rectangle;
    case "ellipse":
    default:
      return PowerPoint.GeometricShapeType.ellipse;
  }
}

/* ── Undo state (one level) ──────────────────────── */

interface PlotSnapshot {
  locations: MapLocation[];
  markerConfig: MarkerConfig;
  shapeBounds: ShapeBounds;
  profile: MapProfile | null;
  calibration: Calibration | null;
}

let lastPlotData: PlotSnapshot | null = null;
let undoData: PlotSnapshot | null = null;

export function canUndoClear(): boolean {
  return undoData !== null;
}

/* ── Slide insertion ─────────────────────────────── */

/** Resolve after `ms` without using the blocked foreground sleep. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Select + scroll to a 1-based slide index so it becomes the active slide. */
function focusSlideByIndex(index1Based: number): Promise<void> {
  return new Promise((resolve) => {
    try {
      Office.context.document.goToByIdAsync(
        index1Based,
        Office.GoToType.Index,
        () => resolve(),
      );
    } catch {
      resolve();
    }
  });
}

/**
 * Insert the base64 PPTX (from `buildMapSlide`) right after the selected slide,
 * adopting the destination theme, then scroll to it (by index) so it becomes
 * the active slide.
 *
 * Returns the inserted slide's **ID** (found by diffing the slide-id set before
 * and after insertion) for reference. Focus uses index navigation — not
 * `setSelectedSlides` — to avoid triggering PowerPoint's Designer pane.
 */
export async function insertMapSlide(pptxBase64: string): Promise<string | null> {
  ensurePpt();
  let newIndex1Based: number | null = null;
  let newSlideId: string | null = null;

  await ppt().run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items/id");
    await ctx.sync();

    const beforeIds = new Set(slides.items.map((s) => s.id));

    const insertOptions: PowerPoint.InsertSlideOptions = {
      formatting: "UseDestinationTheme",
    };
    const targetId = selected.items[0]?.id;
    // Default to inserting after the last slide if nothing is selected.
    let targetArrayIndex = slides.items.length - 1;
    if (targetId) {
      insertOptions.targetSlideId = targetId;
      const idx = slides.items.findIndex((s) => s.id === targetId);
      if (idx >= 0) targetArrayIndex = idx;
    }
    // The new slide lands right after the target → 1-based index + 2.
    newIndex1Based = targetArrayIndex + 2;

    ctx.presentation.insertSlidesFromBase64(pptxBase64, insertOptions);
    await ctx.sync();

    // Re-read the slide list and find the id that wasn't there before.
    const after = ctx.presentation.slides;
    after.load("items/id");
    await ctx.sync();
    const added = after.items.find((s) => !beforeIds.has(s.id));
    newSlideId = added?.id ?? null;
  });

  // Brief settle so the inserted slide is addressable, then scroll to it by
  // index. NOTE: we intentionally do NOT use `setSelectedSlides` here — that
  // selection API triggers PowerPoint's Designer pane to open over the add-in
  // taskpane (which reloads it and loses in-progress state). Index navigation
  // focuses the new slide without waking Designer.
  await delay(500);
  if (newIndex1Based != null) {
    await focusSlideByIndex(newIndex1Based);
  }

  return newSlideId;
}

/**
 * Bounds (POINTS) of the inserted map. Prefers the group named "Map"; falls
 * back to the largest shape on the active slide. Returns null when neither
 * exists (e.g. the map slide isn't the active slide).
 */
export async function getMapBounds(): Promise<ShapeBounds | null> {
  const shapes = await getActiveSlideShapes();
  if (shapes.length === 0) return null;
  const named = shapes.find((s) => s.name === "Map");
  const target =
    named ??
    shapes.reduce(
      (a, b) => (a.width * a.height > b.width * b.height ? a : b),
      shapes[0]!,
    );
  return { left: target.left, top: target.top, width: target.width, height: target.height };
}

/* ── Slide reads ─────────────────────────────────── */

/** Shapes on the active (selected) slide. Positions in POINTS. */
export async function getActiveSlideShapes(): Promise<SlideShapeInfo[]> {
  if (!hasPpt()) return [];
  const out: SlideShapeInfo[] = [];
  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) return;
    const shapes = slide.shapes;
    shapes.load("items/id,items/name,items/left,items/top,items/width,items/height");
    await ctx.sync();
    for (const s of shapes.items) {
      out.push({
        id: s.id,
        name: s.name,
        left: s.left,
        top: s.top,
        width: s.width,
        height: s.height,
      });
    }
  });
  return out;
}

/** Region (state/province) shape names inside the "Map" group (API 1.8). */
export async function getMapRegionNames(): Promise<string[]> {
  if (!hasPpt()) return [];
  const names: string[] = [];
  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) return;
    const shapes = slide.shapes;
    shapes.load("items/name");
    await ctx.sync();

    for (const shape of shapes.items) {
      if (!(shape.name || "").startsWith("Map")) continue;
      try {
        const groupShapes = shape.group.shapes;
        groupShapes.load("items/name");
        await ctx.sync(); // NOSONAR ordered Office.js operations on a shared context
        for (const child of groupShapes.items) {
          const n = child.name || "";
          if (
            n &&
            n !== "MapFrame" &&
            !n.startsWith("Marker:") &&
            !n.startsWith("Label:") &&
            !/^(Group|Rectangle|Freeform)\s*\d*$/i.test(n)
          ) {
            names.push(n);
          }
        }
      } catch {
        logEvent("connector.action.invoke", "warn", {
          connectorSystemName: MAP_CONNECTOR,
          reason: "map-region-read",
        });
      }
    }
  });
  return names.sort((a, b) => a.localeCompare(b));
}

/* ── Marker plotting ─────────────────────────────── */

/**
 * Plot markers on the active slide.
 *
 * @param shapeBounds Bounding box (POINTS) of the inserted "Map" group shape.
 * @param profile     Projection profile (see `getProfile`), or null with calibration.
 * @param calibration Optional affine override; takes priority over `profile`.
 */
export async function plotMarkers(
  locations: MapLocation[],
  markerConfig: MarkerConfig,
  shapeBounds: ShapeBounds,
  profile: MapProfile | null,
  calibration: Calibration | null,
): Promise<PlotResult> {
  ensurePpt();
  const placed: Array<MapLocation & { slideX: number; slideY: number }> = [];
  const skipped: PlotResult["skipped"] = [];

  for (const loc of locations) {
    try {
      let coords;
      if (calibration) {
        coords = engine.projectWithCalibration(loc.lat, loc.lon, calibration);
      } else if (profile) {
        coords = engine.projectToSlide(loc.lat, loc.lon, profile, shapeBounds);
      } else {
        skipped.push({ name: loc.name, reason: "No profile or calibration" });
        continue;
      }
      if (!coords) {
        skipped.push({ name: loc.name, reason: "Outside map bounds" });
        continue;
      }
      placed.push({ ...loc, slideX: coords.x, slideY: coords.y });
    } catch (err) {
      skipped.push({
        name: loc.name,
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (placed.length === 0) return { placed: 0, skipped };

  for (let i = 0; i < placed.length; i += MARKER_BATCH_SIZE) {
    await addMarkerBatch(placed.slice(i, i + MARKER_BATCH_SIZE), markerConfig); // NOSONAR marker batches are added to the slide in order
  }

  lastPlotData = {
    locations,
    markerConfig: { ...markerConfig },
    shapeBounds: { ...shapeBounds },
    profile,
    calibration,
  };

  logEvent("connector.action.invoke", "usage", {
    connectorSystemName: MAP_CONNECTOR,
    reason: "map-markers",
  });
  return { placed: placed.length, skipped };
}

/** Add one marker shape; returns its label text box when labels are on. */
function addMarkerShape(
  slide: PowerPoint.Slide,
  marker: MapLocation & { slideX: number; slideY: number },
  config: MarkerConfig,
  type: PowerPoint.GeometricShapeType,
): PowerPoint.Shape | null {
  const shape = slide.shapes.addGeometricShape(type);
  shape.left = marker.slideX - config.size / 2;
  shape.top = marker.slideY - config.size / 2;
  shape.width = config.size;
  shape.height = config.size;
  shape.fill.setSolidColor(config.color);
  shape.lineFormat.visible = false;
  if (config.borderWidth > 0 && config.borderColor) {
    shape.lineFormat.visible = true;
    shape.lineFormat.color = config.borderColor;
    shape.lineFormat.weight = config.borderWidth;
  }
  shape.name = MARKER_PREFIX + marker.name;

  if (!config.label) return null;
  const labelWidth = Math.max(config.size * 8, 60);
  const labelHeight = 24;
  const textBox = slide.shapes.addTextBox(marker.name);
  textBox.left = marker.slideX - labelWidth / 2;
  textBox.top = marker.slideY + config.size / 2 + 1;
  textBox.width = labelWidth;
  textBox.height = labelHeight;
  textBox.name = LABEL_PREFIX + marker.name;
  return textBox;
}

/** Style label boxes: transparent box, then small bold grey text. */
async function styleLabelBoxes(
  ctx: PowerPoint.RequestContext,
  labelBoxes: PowerPoint.Shape[],
): Promise<void> {
  for (const textBox of labelBoxes) {
    try {
      textBox.fill.clear();
      textBox.lineFormat.weight = 0;
    } catch {
      /* ignore */
    }
  }
  try {
    await ctx.sync();
  } catch {
    /* ignore */
  }

  for (const textBox of labelBoxes) textBox.textFrame.load("textRange");
  await ctx.sync();

  for (const textBox of labelBoxes) {
    try {
      textBox.textFrame.textRange.font.size = 8;
      textBox.textFrame.textRange.font.color = "#333333";
      textBox.textFrame.textRange.font.bold = true;
    } catch {
      /* ignore */
    }
  }
  try {
    await ctx.sync();
  } catch {
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: MAP_CONNECTOR,
      reason: "map-label-sync",
    });
  }
}

async function addMarkerBatch(
  markers: Array<MapLocation & { slideX: number; slideY: number }>,
  config: MarkerConfig,
): Promise<void> {
  const type = markerShapeType(config.shape);

  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) throw new Error("No slide selected");

    const labelBoxes: PowerPoint.Shape[] = [];
    for (const marker of markers) {
      const textBox = addMarkerShape(slide, marker, config, type);
      if (textBox) labelBoxes.push(textBox);
    }

    await ctx.sync();

    if (labelBoxes.length > 0) {
      await styleLabelBoxes(ctx, labelBoxes);
    }
  });
}

/** Remove all marker/label shapes this add-in created from the active slide. */
export async function clearMarkers(): Promise<number> {
  ensurePpt();
  if (lastPlotData) undoData = { ...lastPlotData };

  let removed = 0;
  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) return;
    const shapes = slide.shapes;
    shapes.load("items/name,items/id");
    await ctx.sync();

    const toDelete = shapes.items.filter(
      (s) =>
        s.name && (s.name.startsWith(MARKER_PREFIX) || s.name.startsWith(LABEL_PREFIX)),
    );
    for (const shape of toDelete) {
      shape.delete();
      removed++;
    }
    await ctx.sync();
  });
  return removed;
}

/** Re-plot the markers removed by the last `clearMarkers()`. */
export async function undoClear(): Promise<PlotResult> {
  if (!undoData) throw new Error("Nothing to undo.");
  const { locations, markerConfig, shapeBounds, profile, calibration } = undoData;
  undoData = null;
  return plotMarkers(locations, markerConfig, shapeBounds, profile, calibration);
}

/* ── Region shading ──────────────────────────────── */

/** Fill named region shapes (top-level and inside the "Map" group) one colour. */
export async function fillRegions(
  regionNames: string[],
  fillColor: string,
): Promise<number> {
  return fillRegionsMultiColor(
    Object.fromEntries(regionNames.map((n) => [n, fillColor])),
  );
}

/**
 * Fill regions each with its own colour. Reads "Map" group children via
 * `shape.group.shapes` (PowerPointApi 1.8) — no ungrouping.
 */
export async function fillRegionsMultiColor(
  regionColorMap: Record<string, string>,
): Promise<number> {
  ensurePpt();
  const nameToColor = new Map<string, string>();
  for (const [name, color] of Object.entries(regionColorMap)) {
    nameToColor.set(name.toLowerCase(), color);
  }

  let filled = 0;
  await ppt().run(async (ctx) => {
    const selected = ctx.presentation.getSelectedSlides();
    selected.load("items");
    await ctx.sync();
    const slide = selected.items[0];
    if (!slide) return;
    const shapes = slide.shapes;
    shapes.load("items/name");
    await ctx.sync();

    // Top-level shapes.
    for (const shape of shapes.items) {
      const color = nameToColor.get((shape.name || "").toLowerCase());
      if (color) {
        shape.fill.setSolidColor(color);
        filled++;
      }
    }

    // Children of the "Map" group.
    for (const shape of shapes.items) {
      if (!(shape.name || "").startsWith("Map")) continue;
      try {
        const groupShapes = shape.group.shapes;
        groupShapes.load("items/name");
        await ctx.sync(); // NOSONAR ordered Office.js operations on a shared context
        for (const child of groupShapes.items) {
          const color = nameToColor.get((child.name || "").toLowerCase());
          if (color) {
            child.fill.setSolidColor(color);
            filled++;
          }
        }
      } catch {
        logEvent("connector.action.invoke", "warn", {
          connectorSystemName: MAP_CONNECTOR,
          reason: "map-group-read",
        });
      }
    }

    await ctx.sync();
  });

  logEvent("connector.action.invoke", "usage", {
    connectorSystemName: MAP_CONNECTOR,
    reason: "map-fill",
  });
  return filled;
}
